import React, { createContext, useContext } from 'react';
import ReactPlayer from 'react-player';
import type { RefObject, MouseEvent } from 'react';

import type { VideoPlayerAction } from '../hooks/useVideoPlayerState';

type State = {
	src: string;
	poster?: string;
	thumbnail: string;
	volume: number;
	duration: number;
	fullscreen: boolean;
	playbackRate: number;
	playedSeconds: number;
	playingState: boolean;
	minimized: boolean;
	isDraggable: boolean;
	isCollapsed: boolean;
	controlsVisible: boolean;
	showCloseButton: boolean;
	showMinimize: boolean;
	showVolume: boolean;
	seeking: boolean;
	playerRef: RefObject<ReactPlayer>;
	previousVolumeRef: React.MutableRefObject<number>;
};

interface Methods {
	dispatch: React.Dispatch<VideoPlayerAction>;
	handleSeek: (seconds: number) => void;
	handleReady: () => void;
	handleProgress: (progress: { played: number; playedSeconds: number }) => void;
	handleDuration: (duration: number) => void;
	handleTogglePlay: () => void;
	handleToggleSound: () => void;
	handleVolumeChange: (volume: number) => void;
	handlePlaybackRateChange: (rate: number) => void;
	handleMinimizeToStatic: () => void;
	handleMinimizeToPIP: () => void;
	handleExpandCollapsed: () => void;
	handleCloseIconClick: () => void;
	handleCloseClick: () => void;
	handleExpandClick: (event: MouseEvent) => void;
	handleVideoMouseEnter: () => void;
	handleVideoMouseLeave: () => void;
	handleToggleFullscreen: () => void;
	showControls: () => void;
}

type VideoPlayerContextData = State & Methods;

interface VideoPlayerProviderProps {
	children: JSX.Element | JSX.Element[];
	value: VideoPlayerContextData;
}

const VideoPlayerContext = createContext<VideoPlayerContextData>({} as VideoPlayerContextData);

export function VideoPlayerProvider({ children, value }: Readonly<VideoPlayerProviderProps>): JSX.Element {
	return <VideoPlayerContext.Provider value={value}>{children}</VideoPlayerContext.Provider>;
}

export function useVideoPlayerContext(): VideoPlayerContextData {
	const context = useContext(VideoPlayerContext);
	if (!context) {
		throw new Error('useVideoPlayerContext must be used within VideoPlayerProvider');
	}
	return context;
}

export type { VideoPlayerContextData, State, Methods };
